import type { Permission } from "../../repositories/customPackage";
import {
  getPermissionControl,
  getPermissionDisplayName,
  type PermissionValues,
  type PermissionFieldValue,
} from "../../utils/permissions";
import ToggleSwitch from "../ui/ToggleSwitch";
import { formCheckboxClass, formInputClass } from "../ui/formStyles";
import { cn } from "../../DashboardPage/lib/utils";

interface ExclusivePermissionsFormProps {
  permissions: Permission[];
  values: PermissionValues;
  errors: Record<number, boolean>;
  onChange: (permissionId: number, value: PermissionFieldValue) => void;
}

export default function ExclusivePermissionsForm({
  permissions,
  values,
  errors,
  onChange,
}: ExclusivePermissionsFormProps) {
  if (permissions.length === 0) {
    return <p className="py-8 text-sm text-center text-neutral-500">No permissions found</p>;
  }

  return (
    <div className="space-y-3">
      {permissions.map((permission) => {
        const value = values[permission.id] ?? {
          access: false,
          limit: "",
          unlimited: false,
        };
        const control = getPermissionControl(permission.name);
        const displayName = getPermissionDisplayName(permission.name);

        return (
          <div
            key={permission.id}
            className="flex flex-col gap-3 p-4 border rounded-lg sm:flex-row sm:items-center sm:justify-between border-neutral-200 bg-neutral-50"
          >
            <span className="text-sm font-medium text-black">{displayName}</span>

            {control === "toggle" ? (
              <div className="flex items-center gap-3">
                <span className="w-8 text-sm text-neutral-600">
                  {value.access ? "Yes" : "No"}
                </span>
                <ToggleSwitch
                  checked={value.access}
                  onChange={(next) => onChange(permission.id, { ...value, access: next })}
                  label={displayName}
                />
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  value={value.limit}
                  onChange={(e) => onChange(permission.id, { ...value, limit: e.target.value })}
                  disabled={value.unlimited}
                  required={!value.unlimited}
                  aria-label={displayName}
                  aria-invalid={!!errors[permission.id]}
                  className={cn(formInputClass, "w-32", errors[permission.id] && "border-danger-500")}
                  placeholder="Enter limit"
                  min="1"
                />
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value.unlimited}
                    onChange={(e) =>
                      onChange(permission.id, {
                        ...value,
                        unlimited: e.target.checked,
                        // Clear the input when switching to unlimited.
                        limit: e.target.checked ? "" : value.limit,
                      })
                    }
                    className={formCheckboxClass}
                  />
                  <span className="text-sm text-neutral-700">Unlimited</span>
                </label>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
